import { ref, computed, watch } from "vue";
import { GetThemeUseCase, UpdateThemeUseCase } from "@/application/use-cases/PreferencesUseCases";
import { ApiUserPreferencesRepository } from "@/infrastructure/repositories/ApiUserPreferencesRepository";
import { logger } from "@/infrastructure/services/LoggerService";
// Create singleton instances
const preferencesRepository = new ApiUserPreferencesRepository();
const getThemeUseCase = new GetThemeUseCase(preferencesRepository);
const updateThemeUseCase = new UpdateThemeUseCase(preferencesRepository);
const THEME_STORAGE_KEY = "opay-theme";
// Read cached theme from localStorage for instant application (no flash)
function getCachedTheme() {
    try {
        const cached = localStorage.getItem(THEME_STORAGE_KEY);
        if (cached === "dark" || cached === "light") {
            return cached;
        }
    }
    catch {
        // localStorage may be unavailable
    }
    return "light";
}
function saveCachedTheme(theme) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
    catch {
        // localStorage may be unavailable
    }
}
// Shared reactive state - initialize from localStorage cache
const currentTheme = ref(getCachedTheme());
const isInitialized = ref(false);
/**
 * Applies the theme to the document element
 * Adds/removes 'dark' class for Tailwind dark mode
 */
function applyTheme(newTheme) {
    if (newTheme === "dark") {
        document.documentElement.classList.add("dark");
    }
    else {
        document.documentElement.classList.remove("dark");
    }
    saveCachedTheme(newTheme);
    logger.debug(`[usePreferences] Applied theme to document: ${newTheme}`);
}
/**
 * Initialize theme from backend (can be called from router or after login)
 * This function updates the shared theme state and applies it to the document
 */
export async function initializeTheme() {
    if (isInitialized.value) {
        return; // Already initialized
    }
    // Apply cached theme immediately (no flash)
    applyTheme(currentTheme.value);
    try {
        const storedTheme = await getThemeUseCase.execute();
        // Only update if different from cached value
        if (storedTheme !== currentTheme.value) {
            currentTheme.value = storedTheme;
            applyTheme(storedTheme);
        }
        isInitialized.value = true;
        logger.debug("[usePreferences] Initialized theme:", storedTheme);
    }
    catch (error) {
        logger.debug("[usePreferences] Error initializing theme:", error);
        isInitialized.value = true;
    }
}
/**
 * Composable for user theme preferences management (OPREF)
 */
export function usePreferences() {
    /**
     * Current theme
     */
    const theme = computed(() => currentTheme.value);
    /**
     * Toggles between light and dark themes
     */
    const toggleTheme = async () => {
        const newTheme = theme.value === "light" ? "dark" : "light";
        await setTheme(newTheme);
    };
    /**
     * Sets a specific theme
     */
    const setTheme = async (newTheme) => {
        currentTheme.value = newTheme;
        applyTheme(newTheme);
        await updateThemeUseCase.execute(newTheme);
        logger.debug(`[usePreferences] Set theme to: ${newTheme}`);
    };
    /**
     * Gets theme from backend (useful when logging in from Keycloak)
     * Fetches theme preference and updates the current theme
     */
    const getTheme = async () => {
        try {
            const fetchedTheme = await getThemeUseCase.execute();
            currentTheme.value = fetchedTheme;
            isInitialized.value = true;
            logger.debug(`[usePreferences] Fetched theme from backend: ${fetchedTheme}`);
            return fetchedTheme;
        }
        catch (error) {
            logger.debug("[usePreferences] Error fetching theme from backend:", error);
            // Fallback to light theme
            currentTheme.value = "light";
            isInitialized.value = true;
            return "light";
        }
    };
    // Watch for theme changes and apply them to document
    watch(theme, applyTheme, { immediate: true });
    return {
        theme,
        isInitialized,
        toggleTheme,
        setTheme,
        getTheme,
    };
}
